import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

const DEFAULT_MAX_HEIGHT = 280;
const GAP = 4;
const MIN_VISIBLE_HEIGHT = 120;

/** Above MUI Dialog (1300) and snackbar (1400). */
export const PORTAL_DROPDOWN_Z_INDEX = 1500;
export const PORTAL_DROPDOWN_BACKDROP_Z_INDEX = PORTAL_DROPDOWN_Z_INDEX - 1;

export const usePortalDropdownPosition = (open, maxHeight = DEFAULT_MAX_HEIGHT) => {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - GAP;
    const spaceAbove = rect.top - GAP;
    const openUpward = spaceBelow < maxHeight && spaceAbove > spaceBelow;

    const availableSpace = openUpward ? spaceAbove : spaceBelow;
    const resolvedMaxHeight = Math.min(maxHeight, Math.max(availableSpace, MIN_VISIBLE_HEIGHT));

    // Prefer bottom-anchoring when opening upward so a short menu sits
    // tightly above the trigger (avoid using maxHeight as assumed height).
    setMenuStyle(
      openUpward
        ? {
            position: "fixed",
            top: "auto",
            bottom: Math.max(GAP, window.innerHeight - rect.top + GAP),
            left: rect.left,
            width: rect.width,
            maxHeight: resolvedMaxHeight,
            zIndex: PORTAL_DROPDOWN_Z_INDEX,
          }
        : {
            position: "fixed",
            top: rect.bottom + GAP,
            bottom: "auto",
            left: rect.left,
            width: rect.width,
            maxHeight: resolvedMaxHeight,
            zIndex: PORTAL_DROPDOWN_Z_INDEX,
          }
    );
  }, [maxHeight]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return;
    }
    updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;

    const handleReposition = () => updatePosition();
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);

    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [open, updatePosition]);

  return { triggerRef, menuRef, menuStyle, updatePosition };
};
